import React from 'react'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Group } from '../../types/ffe'
import { GroupHeader } from './GroupHeader'
import { SortableLineItem } from '../LineItem/SortableLineItem'
import { useFFEStore } from '../../store/ffeStore'
import { AddLineItemButton } from '../LineItem/AddLineItemButton'
import { useShallow } from 'zustand/react/shallow'

interface Props {
  group: Group
  depth: number
}

export const GroupContainer: React.FC<Props> = ({ group, depth }) => {
  const items = useFFEStore(
    useShallow((state) =>
      state.document.lineItems.filter((item) => item.groupId === group.id)
    )
  )

  const isCollapsed = !!group.isCollapsed
  const updateGroup = useFFEStore((state) => state.updateGroup)
  const setIsCollapsed = (isCollapsed: boolean) => {
    updateGroup(group.id, { isCollapsed })
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: group.id,
    data: {
      type: 'group',
      group
    }
  })

  const style = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, scaleY: 1 } : null
    ),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const groups = useFFEStore(
    useShallow((state) =>
      state.document.groups.filter((g) => g.groupId === group.id)
    )
  )

  const newDepth = depth + 1

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-4 ${isCollapsed ? 'border-b rounded-lg border-r' : 'pb-8'} border-l border-t border-b border-gray-200  rounded-bl-lg rounded-tl-lg`}
      {...attributes}
      id={`group-${group.id}`} // Add ID for scrolling
    >
      <GroupHeader
        group={group}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        dragHandleProps={listeners}
      />

      {!isCollapsed && (
        <div className="pl-4 md:pl-8">
          <SortableContext
            items={groups}
            strategy={verticalListSortingStrategy}
          >
            {groups.map((subGroup) => (
              <GroupContainer
                key={subGroup.id}
                group={subGroup}
                depth={newDepth}
              />
            ))}
          </SortableContext>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <SortableLineItem key={item.id} item={item} group={group} />
            ))}
          </SortableContext>
          <AddLineItemButton depth={depth} groupId={group.id} />
        </div>
      )}
    </div>
  )
}
